// src/timing/timing-state.js

import {
    toInternalSpeedLinear,
    fromInternalSpeedLinear,
    toInternalAccelLinear,
    toInternalSpeedRotary,
    fromInternalSpeedRotary
} from "./units.js";

import {
    computeMotionCycleTime,
    isFinitePositive
} from "./motion-profile.js";

import {
    getNodeData,
    getNodeTypeFromData
} from "../flow/flow-node-html.js";
import { getDiscreteIoCompletionMode } from "../generation/discrete-io-completion.js";

export function findAxis(axisId, project) {
    return project.axes.find(a => a.id === axisId) || null;
}

export function findMotion(motionId, project) {
    for (const axis of project.axes) {
        const motion = axis.motions.find(m => m.id === motionId);
        if (motion) return { axis, motion };
    }
    return null;
}

export function computeAxisMaxSpeedMmPerSec(axis, units) {
    if (!isFinitePositive(axis.axisMaxSpeed)) return null;
    if (axis.motionType === "linear") {
        return toInternalSpeedLinear(axis.axisMaxSpeed, units);
    }
    return toInternalSpeedRotary(axis.axisMaxSpeed, units);
}

export function getAxisMotionMaxSpeed(axis) {
    let maxSpeed = null;
    for (const motion of axis.motions) {
        if (!isFinitePositive(motion.maxSpeed)) continue;
        if (maxSpeed == null || motion.maxSpeed > maxSpeed) {
            maxSpeed = motion.maxSpeed;
        }
    }
    return maxSpeed;
}

export function totalCycleSec(computed) {
    let max = 0;
    for (const t of Object.values(computed.motionTimes)) {
        if (t.endSec > max) max = t.endSec;
    }
    for (const t of Object.values(computed.subprocessTimes || {})) {
        if (t.endSec > max) max = t.endSec;
    }
    for (const t of Object.values(computed.actuatorMotionTimes || {})) {
        if (t.endSec > max) max = t.endSec;
    }
    return max;
}

export function buildTimingGridMetrics(totalSec, pxPerSec) {
    return {
        trackWidth: Math.max(800, Math.ceil(Math.max(Number(totalSec) || 0, 0) * pxPerSec) + 20),
        stepSec: buildRulerStep(pxPerSec)
    };
}

export function buildTimingGridViewState(project, computed, selectedAxisId, pxPerSec) {
    const totalSec = Math.max(totalCycleSec(computed || { motionTimes: {}, subprocessTimes: {} }), 1);
    const { trackWidth } = buildTimingGridMetrics(totalSec, pxPerSec);

    return {
        totalSec,
        pxPerSec,
        trackWidth,
        rulerMarks: buildTimingRulerMarks(totalSec, pxPerSec),
        axisRows: (project?.axes || []).map((axis) => {
            const axisMotionMaxSpeed = getAxisMotionMaxSpeed(axis);
            const speedUnit = axis.motionType === "linear"
                ? project?.units?.speedLinear
                : (project?.units?.speedRotary || "deg/s");
            const axisMaxSpeedText = axisMotionMaxSpeed != null
                ? `${axisMotionMaxSpeed} ${speedUnit}`
                : "—";
            return {
                axis,
                isSelected: axis.id === selectedAxisId,
                axisMotionMaxSpeed,
                speedUnit,
                axisMaxSpeedText,
                showDetail: axis.id === selectedAxisId
            };
        })
    };
}

export function buildTimingRulerMarks(totalSec, pxPerSec) {
    const { stepSec } = buildTimingGridMetrics(totalSec, pxPerSec);
    const marks = [];
    for (let sec = 0; sec <= totalSec + stepSec; sec += stepSec) {
        marks.push({
            sec,
            leftPx: sec * pxPerSec,
            label: sec.toFixed(stepSec < 1 ? 1 : 0) + "s"
        });
    }
    return marks;
}

export function buildTrackGridStyle(pxPerSec) {
    const stepSec = buildRulerStep(pxPerSec);
    const spacingPx = Math.max(stepSec * pxPerSec, 1);
    return {
        stepSec,
        spacingPx,
        backgroundImage: "repeating-linear-gradient(to right, transparent, transparent "
            + Math.max(spacingPx - 1, 0)
            + "px, #f0f0f0 "
            + Math.max(spacingPx - 1, 0)
            + "px, #f0f0f0 "
            + spacingPx
            + "px)",
        backgroundSize: spacingPx + "px 100%",
        backgroundPosition: "0 0",
        backgroundRepeat: "repeat"
    };
}

export function getSubprocessNodeDuration(data, project) {
    const subprocesses = project.flow?.subprocesses || [];
    const meta = data?.subprocessId ? subprocesses.find(p => p.id === data.subprocessId) : null;
    const duration = data?.durationSec != null ? data.durationSec : (meta?.durationSec ?? 1);
    const num = Number(duration);
    return isFinite(num) && num > 0 ? num : 0;
}

export function getWaitNodeDuration(data) {
    const num = Number(data?.durationSec);
    return isFinite(num) && num > 0 ? num : 0;
}

export function findActuatorAction(actuatorId, actionId, project) {
    const actuator = (project.actuators || []).find(a => a.id === actuatorId);
    if (!actuator) return { actuator: null, action: null };
    const action = (actuator.actions || []).find(a => a.id === actionId) || null;
    return { actuator, action };
}

export function getActuatorMotionNodeDuration(data, project) {
    if (data?.durationSec != null) {
        const num = Number(data.durationSec);
        return isFinite(num) && num > 0 ? num : 0;
    }
    const { action } = findActuatorAction(data?.actuatorId, data?.actionId, project);
    if (getDiscreteIoCompletionMode(action) === "ignore") return 0;
    const num = Number(action?.timeoutSec);
    return isFinite(num) && num > 0 ? num : 1;
}

function isActuatorMotionNode(node, project, nodeIdToMotionId) {
    const data = getNodeData(node);
    if (getNodeTypeFromData(data) !== "motion") return false;
    if (!data.actuatorId || !data.actionId) return false;
    const actuator = (project?.actuators || []).find((item) => item.id === data.actuatorId);
    if (actuator?.controlMethod) return actuator.controlMethod === "discreteIo";
    return !data.motionId && !nodeIdToMotionId[String(node.id)];
}

// Finds the nearest chain-type (subprocess/actuator-motion) ancestor that is still
// unresolved in this pass, so a node isn't computed with a stale/zero anchor before
// its own predecessor in the same pending batch has been resolved.
function findPendingChainBlocker(homeData, connection, pendingSet, project, nodeIdToMotionId, visited = new Set()) {
    const sourceNodeId = String(connection?.node || "");
    if (!sourceNodeId || visited.has(sourceNodeId)) return null;
    const sourceNode = homeData[sourceNodeId];
    if (!sourceNode) return null;
    const data = getNodeData(sourceNode);
    const nodeType = getNodeTypeFromData(data);
    const nextVisited = new Set(visited);
    nextVisited.add(sourceNodeId);

    if (nodeType === "subprocess" || nodeType === "wait" || isActuatorMotionNode(sourceNode, project, nodeIdToMotionId)) {
        return pendingSet.has(sourceNodeId) ? sourceNodeId : null;
    }
    if (nodeType === "condition") {
        const incoming = getNodeIncomingConnections(sourceNode);
        if (incoming.length === 0) return null;
        return findPendingChainBlocker(homeData, incoming[0], pendingSet, project, nodeIdToMotionId, nextVisited);
    }
    return null;
}

export function buildSubprocessTimingItems(subprocessTimes, subprocesses = []) {
    return Object.entries(subprocessTimes || {})
        .map(([nodeId, times]) => ({
            nodeId,
            times,
            meta: times?.subprocessId ? subprocesses.find((item) => item.id === times.subprocessId) || null : null
        }))
        .sort((a, b) => (a.times?.startSec || 0) - (b.times?.startSec || 0));
}

export function buildSubprocessTimingRowState(subprocessTimes, subprocesses = [], pxPerSec) {
    return buildSubprocessTimingItems(subprocessTimes, subprocesses).map((item) => ({
        ...item,
        x: item.times.startSec * pxPerSec,
        width: Math.max(item.times.durationSec * pxPerSec, 1),
        label: item.meta?.name || item.times.subprocessName || item.times.waitLabel || "待機",
        timeLabel: `${item.times.startSec.toFixed(2)}→${item.times.endSec.toFixed(2)}s`
    }));
}

export function buildAxisTimingCurve(axis, computed, project) {
    const items = (axis?.motions || [])
        .map((motion) => ({
            motion,
            times: computed?.motionTimes?.[motion.id],
            err: computed?.motionErrors?.[motion.id] || ""
        }))
        .sort((a, b) => {
            const ta = a.times ? a.times.startSec : Infinity;
            const tb = b.times ? b.times.startSec : Infinity;
            return ta - tb;
        });

    let position = 0;
    const curve = [[0, 0]];
    for (const { motion, times } of items) {
        if (!times) continue;
        curve.push([times.startSec, position]);
        const distance = Number(motion.distance);
        position += Number.isFinite(distance) ? distance : 0;
        curve.push([times.motionEndSec, position]);
        if (times.waitTime > 0) {
            curve.push([times.motionEndSec + times.waitTime, position]);
        }
    }

    const totalSec = totalCycleSec(computed || { motionTimes: {}, subprocessTimes: {} });
    curve.push([totalSec, position]);
    const positions = curve.map(([, value]) => value);
    const minPos = Math.min(...positions);
    const maxPos = Math.max(...positions);
    const posRange = maxPos - minPos;

    return {
        items,
        curve,
        minPos,
        maxPos,
        posRange,
        totalDist: posRange,
        motionLookup: new Map((project?.axes || []).flatMap((projectAxis) =>
            (projectAxis.motions || []).map((motion) => [motion.id, { axis: projectAxis, motion }])
        ))
    };
}

export function buildAxisCurveTrackState(axis, computed, project, pxPerSec, trackHeight = 100) {
    const curveVm = buildAxisTimingCurve(axis, computed, project);
    const padding = { top: 18, bottom: 14, left: 0, right: 0 };
    const innerHeight = trackHeight - padding.top - padding.bottom;

    function projectY(position) {
        if (curveVm.posRange === 0) return padding.top + innerHeight / 2;
        return padding.top + (1 - (position - curveVm.minPos) / curveVm.posRange) * innerHeight;
    }

    return {
        ...curveVm,
        padding,
        innerHeight,
        regions: curveVm.items
            .filter(({ times }) => !!times)
            .map(({ motion, times, err }) => {
                let label = motion.name;
                if (motion.dependency) {
                    const src = curveVm.motionLookup.get(motion.dependency.sourceMotionId) || null;
                    if (src) label += ` ←${src.axis.name}/${src.motion.name}+${(motion.dependency.offsetSec || 0).toFixed(2)}s`;
                }
                if (motion.startConditionText) label += ` [${motion.startConditionText}]`;
                return {
                    motion,
                    times,
                    err,
                    x: times.startSec * pxPerSec,
                    motionWidth: times.cycleTime * pxPerSec,
                    waitWidth: times.waitTime * pxPerSec,
                    label,
                    timeLabel: `${times.startSec.toFixed(2)}→${times.endSec.toFixed(2)}s`
                };
            }),
        polylinePoints: curveVm.curve.length > 1 && curveVm.totalDist > 0
            ? curveVm.curve.map(([timeSec, position]) => `${(timeSec * pxPerSec).toFixed(2)},${projectY(position).toFixed(2)}`).join(" ")
            : "",
        yScaleTopPx: padding.top - 1,
        yScaleBottomPx: padding.top + innerHeight - 8
    };
}

export function buildMotionReferenceOptions(project, excludedMotionId = "") {
    const options = [];
    for (const axis of project?.axes || []) {
        for (const motion of axis.motions || []) {
            if (motion.id === excludedMotionId) continue;
            options.push({
                value: motion.id,
                label: `${axis.name} / ${motion.name}`
            });
        }
    }
    return options;
}

export function buildMotionDetailRowState(axis, motion, computed, project) {
    const times = computed?.motionTimes?.[motion.id];
    const err = computed?.motionErrors?.[motion.id];
    const startMode = motion.dependency ? "ref" : "absolute";
    const selectedSourceMotionId = motion.dependency?.sourceMotionId || "";

    return {
        times,
        err,
        startMode,
        sourceMotionOptions: buildMotionReferenceOptions(project, motion.id).map((option) => ({
            ...option,
            selected: option.value === selectedSourceMotionId
        })),
        adjustedMaxText: times && times.adjustedMaxSpeed != null
            ? formatAdjustedMax(axis, times.adjustedMaxSpeed, project?.units || {})
            : null
    };
}

function buildRulerStep(pxPerSec) {
    const candidates = [0.1, 0.2, 0.5, 1, 2, 5, 10, 30, 60];
    for (const candidate of candidates) {
        if (candidate * pxPerSec >= 80) return candidate;
    }
    return 60;
}

function formatAdjustedMax(axis, adjustedInternal, units) {
    if (axis.motionType === "linear") {
        return fromInternalSpeedLinear(adjustedInternal, units).toFixed(2) + " " + units.speedLinear;
    }
    return adjustedInternal.toFixed(2);
}

function getNodeIncomingConnections(node) {
    const connections = [];
    if (!node?.inputs) return connections;
    for (const inputName in node.inputs) {
        for (const conn of node.inputs[inputName].connections) {
            connections.push({ ...conn, targetInput: inputName });
        }
    }
    return connections;
}

function formatConditionBranchText(data, outputName) {
    const conditionText = (data.conditionText || "条件").trim();
    let branchText;
    if (outputName === "output_2") branchText = data.falseLabel || "OFF";
    else if (outputName === "output_3") branchText = `${data.timeoutLabel || "TIMEOUT"} ${data.timeoutSec ?? 0}s`;
    else branchText = data.trueLabel || "ON";
    branchText = String(branchText).trim();
    return conditionText ? `${conditionText} [${branchText}]` : branchText;
}

export function resolveTimingAnchor(homeData, connection, computed, project, nodeIdToMotionId, visited = new Set()) {
    const sourceNodeId = String(connection?.node || "");
    if (!sourceNodeId || visited.has(sourceNodeId)) {
        return { endSec: 0, conditionParts: [], warning: "循環参照" };
    }
    const sourceNode = homeData[sourceNodeId];
    if (!sourceNode) return { endSec: 0, conditionParts: [] };
    const data = getNodeData(sourceNode);
    const nodeType = getNodeTypeFromData(data);
    const outputName = connection.input || connection.output || "output_1";
    const nextVisited = new Set(visited);
    nextVisited.add(sourceNodeId);

    if (nodeType === "motion") {
        const motionId = data.motionId || nodeIdToMotionId[sourceNodeId] || "";
        if (isActuatorMotionNode(sourceNode, project, nodeIdToMotionId)) {
            return { endSec: computed.actuatorMotionTimes?.[sourceNodeId]?.endSec ?? 0, conditionParts: [] };
        }
        if (motionId) {
            return { endSec: computed.motionTimes[motionId]?.endSec ?? 0, conditionParts: [] };
        }
        return { endSec: computed.actuatorMotionTimes?.[sourceNodeId]?.endSec ?? 0, conditionParts: [] };
    }
    if (nodeType === "subprocess") {
        return { endSec: computed.subprocessTimes[sourceNodeId]?.endSec ?? 0, conditionParts: [] };
    }
    if (nodeType === "wait") {
        return { endSec: computed.subprocessTimes[sourceNodeId]?.endSec ?? 0, conditionParts: [] };
    }
    if (nodeType === "start") {
        return { endSec: 0, conditionParts: [(data.label || "開始").trim()].filter(Boolean) };
    }
    if (nodeType === "condition") {
        const incoming = getNodeIncomingConnections(sourceNode);
        let base = { endSec: 0, conditionParts: [] };
        if (incoming.length > 0) {
            base = resolveTimingAnchor(homeData, incoming[0], computed, project, nodeIdToMotionId, nextVisited);
        }
        return {
            endSec: base.endSec,
            conditionParts: [...(base.conditionParts || []), formatConditionBranchText(data, outputName)].filter(Boolean),
            warning: base.warning
        };
    }
    return { endSec: 0, conditionParts: [] };
}

export function recomputeSubprocessTimes(homeData, project, computed, nodeIdToMotionId) {
    computed.subprocessTimes = {};
    computed.actuatorMotionTimes = {};
    const pending = Object.values(homeData)
        .filter((node) => {
            const data = getNodeData(node);
            return getNodeTypeFromData(data) === "subprocess"
                || getNodeTypeFromData(data) === "wait"
                || isActuatorMotionNode(node, project, nodeIdToMotionId);
        })
        .map((node) => String(node.id));
    const attempts = {};
    let progressed = true;
    while (pending.length && progressed) {
        progressed = false;
        for (let index = pending.length - 1; index >= 0; index -= 1) {
            const nodeId = pending[index];
            const node = homeData[nodeId];
            const data = getNodeData(node);
            const isActuatorMotion = isActuatorMotionNode(node, project, nodeIdToMotionId);
            const targetTable = isActuatorMotion ? computed.actuatorMotionTimes : computed.subprocessTimes;
            const subprocesses = project.flow?.subprocesses || [];
            const meta = data?.subprocessId ? subprocesses.find(p => p.id === data.subprocessId) : null;
            const isWaitNode = getNodeTypeFromData(data) === "wait";
            const incoming = getNodeIncomingConnections(node);
            if (incoming.length === 0) {
                const durationSec = isActuatorMotion
                    ? getActuatorMotionNodeDuration(data, project)
                    : isWaitNode
                        ? getWaitNodeDuration(data)
                        : getSubprocessNodeDuration(data, project);
                targetTable[nodeId] = isActuatorMotion
                    ? {
                        startSec: 0,
                        endSec: durationSec,
                        cycleTime: durationSec,
                        durationSec,
                        actuatorId: data.actuatorId || "",
                        actionId: data.actionId || "",
                        conditionParts: []
                    }
                    : {
                        startSec: 0,
                        endSec: durationSec,
                        cycleTime: durationSec,
                        durationSec,
                        subprocessId: data.subprocessId || "",
                        subprocessName: meta?.name || data.subprocessName || data.label || "サブプロセス",
                        waitLabel: isWaitNode ? (data.label || "待機") : "",
                        conditionParts: []
                    };
                pending.splice(index, 1);
                progressed = true;
                continue;
            }
            const pendingSet = new Set(pending);
            pendingSet.delete(nodeId);
            if (findPendingChainBlocker(homeData, incoming[0], pendingSet, project, nodeIdToMotionId)) continue;

            const anchor = resolveTimingAnchor(homeData, incoming[0], computed, project, nodeIdToMotionId);
            if (anchor.warning === "循環参照" && !targetTable[nodeId]) {
                attempts[nodeId] = (attempts[nodeId] || 0) + 1;
                if (attempts[nodeId] < 2) continue;
            }
            const durationSec = isActuatorMotion
                ? getActuatorMotionNodeDuration(data, project)
                : isWaitNode
                    ? getWaitNodeDuration(data)
                    : getSubprocessNodeDuration(data, project);
            const startSec = anchor.endSec || 0;
            targetTable[nodeId] = isActuatorMotion
                ? {
                    startSec,
                    endSec: startSec + durationSec,
                    cycleTime: durationSec,
                    durationSec,
                    actuatorId: data.actuatorId || "",
                    actionId: data.actionId || "",
                    conditionParts: anchor.conditionParts || []
                }
                : {
                    startSec,
                    endSec: startSec + durationSec,
                    cycleTime: durationSec,
                    durationSec,
                    subprocessId: data.subprocessId || "",
                    subprocessName: meta?.name || data.subprocessName || data.label || "サブプロセス",
                    waitLabel: isWaitNode ? (data.label || "待機") : "",
                    conditionParts: anchor.conditionParts || []
                };
            pending.splice(index, 1);
            progressed = true;
        }
    }
}

export function recomputeAll({ project, transientWarnings = [], flowDataSnapshot = {}, nodeIdToMotionId = {} }) {
    const computed = {
        motionTimes: {},
        subprocessTimes: {},
        actuatorMotionTimes: {},
        errors: [],
        motionErrors: {}
    };

    // Push transient warnings
    const localWarnings = [...transientWarnings];
    while (localWarnings.length) {
        const w = localWarnings.shift();
        computed.motionErrors[w.motionId] = w.msg;
    }

    // Flatten all motions
    const allMotions = [];
    for (const axis of project.axes) {
        for (const motion of axis.motions) {
            allMotions.push({ axis, motion });
        }
    }

    // Validate dependency references
    const motionIdSet = new Set(allMotions.map(x => x.motion.id));
    for (const { motion } of allMotions) {
        if (motion.dependency && !motionIdSet.has(motion.dependency.sourceMotionId)) {
            computed.motionErrors[motion.id] = "参照元の動作が存在しません";
            computed.errors.push(`「${motion.name}」: 参照元動作が存在しません`);
        }
    }

    // Topological sort
    const incoming = {};
    const outgoing = {};
    for (const { motion } of allMotions) {
        incoming[motion.id] = [];
        outgoing[motion.id] = [];
    }
    for (const { motion } of allMotions) {
        if (motion.dependency && motionIdSet.has(motion.dependency.sourceMotionId)) {
            incoming[motion.id].push(motion.dependency.sourceMotionId);
            outgoing[motion.dependency.sourceMotionId].push(motion.id);
        }
    }
    const queue = allMotions.filter(({ motion }) => incoming[motion.id].length === 0).map(x => x.motion.id);
    const ordered = [];
    const remaining = Object.fromEntries(allMotions.map(({ motion }) => [motion.id, incoming[motion.id].length]));
    while (queue.length) {
        const id = queue.shift();
        ordered.push(id);
        for (const next of outgoing[id]) {
            remaining[next] -= 1;
            if (remaining[next] === 0) queue.push(next);
        }
    }
    if (ordered.length !== allMotions.length) {
        computed.errors.push("循環参照があります");
        for (const { motion } of allMotions) {
            if (!ordered.includes(motion.id)) {
                computed.motionErrors[motion.id] = computed.motionErrors[motion.id] || "循環参照";
            }
        }
    }

    // Compute times in order
    for (const motionId of ordered) {
        const found = findMotion(motionId, project);
        if (!found) continue;
        const { axis, motion } = found;
        const cycle = computeMotionCycleTime(motion, axis, project.units);
        if (cycle.error) {
            computed.motionErrors[motion.id] = cycle.error;
            continue;
        }
        let startSec = 0;
        if (motion.dependency) {
            const srcTimes = computed.motionTimes[motion.dependency.sourceMotionId];
            if (!srcTimes) {
                computed.motionErrors[motion.id] = "参照元が計算できていません";
                continue;
            }
            startSec = srcTimes.endSec + (motion.dependency.offsetSec || 0);
        } else {
            startSec = motion.absoluteStartSec ?? 0;
        }
        const endSec = startSec + cycle.cycleTime + (motion.waitTime || 0);
        computed.motionTimes[motion.id] = {
            startSec,
            endSec,
            cycleTime: cycle.cycleTime,
            accelTime: cycle.accelTime,
            cruiseTime: cycle.cruiseTime,
            decelTime: cycle.decelTime,
            adjustedMaxSpeed: cycle.adjustedMaxSpeed,
            waitTime: motion.waitTime || 0,
            motionEndSec: startSec + cycle.cycleTime
        };
    }

    // Validate axis max speed
    for (const axis of project.axes) {
        const axisMaxMmPerSec = computeAxisMaxSpeedMmPerSec(axis, project.units);
        if (axisMaxMmPerSec == null) continue;
        for (const motion of axis.motions) {
            if (!isFinitePositive(motion.maxSpeed)) continue;
            let internal, displayMax, unitLabel;
            if (axis.motionType === "linear") {
                internal = toInternalSpeedLinear(motion.maxSpeed, project.units);
                if (internal > axisMaxMmPerSec + 1e-6) {
                    const cur = computed.motionErrors[motion.id];
                    displayMax = fromInternalSpeedLinear(axisMaxMmPerSec, project.units);
                    unitLabel = project.units.speedLinear;
                    const msg = `軸最高速度 ${displayMax.toFixed(2)} ${unitLabel} を超えています`;
                    computed.motionErrors[motion.id] = cur ? cur + " / " + msg : msg;
                }
            } else {
                internal = toInternalSpeedRotary(motion.maxSpeed, project.units);
                if (internal > axisMaxMmPerSec + 1e-6) {
                    const cur = computed.motionErrors[motion.id];
                    displayMax = fromInternalSpeedRotary(axisMaxMmPerSec, project.units);
                    unitLabel = project.units.speedRotary || "deg/s";
                    const msg = `軸最高速度 ${displayMax.toFixed(2)} ${unitLabel} を超えています`;
                    computed.motionErrors[motion.id] = cur ? cur + " / " + msg : msg;
                }
            }
        }
    }

    // Recompute subprocesses
    recomputeSubprocessTimes(flowDataSnapshot, project, computed, nodeIdToMotionId);

    return computed;
}
